'use client';
import { MotionMaybe, useMotionControls } from '@/utils/animation';
import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getChildHeadingVariant } from '@/utils/headingLevel';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { MarkDown } from './atoms/MarkDown';

export default function ItemList(props) {
    const {
        Anchor,
        Tagline,
        Header,
        Body,
        Features,
        ColorScheme = 'light',
        VerticalSpacing = 'medium',
        Alignment = 'left', // New prop for alignment
        Animation = 'fade',
        Duration = 0.6,
        AnimationGroup = 'content',
        EditorPreview = false,
    } = props;

    const effectiveColorScheme = ColorScheme.startsWith('box')
        ? 'White'
        : ColorScheme;

    const {
        backgroundColor,
        textColor,
        headerColor,
        iconColor,
        thirdButton,
        secondaryButton,
        overlayColor,
        taglineColor,
        backgroundStyle,
    } = getColorScheme(effectiveColorScheme, props.GradientDirection);

    // Define alignment classes
    let textAlign, containerAlign, iconAlign, buttonAlign, cardAlign;
    switch (Alignment) {
        case 'center':
            textAlign = 'text-center';
            containerAlign = 'items-center';
            cardAlign = 'mx-auto';
            iconAlign = 'flex-col items-center justify-start';
            buttonAlign = 'justify-center';
            break;
        default: // left
            textAlign = 'text-left mb-3';
            containerAlign = 'items-start mr-auto';
            cardAlign = 'mx-0';
            iconAlign = 'flex-row items-start';
            buttonAlign = 'justify-start';
            break;
    }

    const vSpace = getVerticalSpacing(VerticalSpacing);
    const featureHeadingVariant = getChildHeadingVariant(Header);
    const { hasAny: hasCTAs, element: CTAs } = getCTA(props, {
        secondaryVariant: secondaryButton,
        outlineVariant: thirdButton,
        buttonAlign,
        wrapSpacing: 'mt-10 lg:mt-16',
        buttonsClassName: 'flex flex-wrap items-center gap-4',
    });
    const durationVal = Number(Duration) || 0.6;

    const normalizedGroup = (() => {
        const raw = String(AnimationGroup ?? '')
            .toLowerCase()
            .trim();
        if (['feature', 'features', 'items', 'list'].includes(raw))
            return 'features';
        if (['cta', 'ctas', 'buttons', 'button'].includes(raw)) return 'ctas';
        if (raw === 'all') return 'all';
        return 'content';
    })();
    const normalizedPreset = (() => {
        const raw = String(Animation ?? '')
            .toLowerCase()
            .trim();
        const map = {
            none: 'none',
            '': 'fade',
            fade: 'fade',
            'fade in': 'fade',
            slide: 'slide',
            'slide up': 'slide',
            zoom: 'zoom',
            'zoom in': 'zoom',
            stagger: 'stagger',
            cascade: 'stagger',
        };
        return map[raw] ?? 'fade';
    })();

    const {
        enableAnim,
        isStagger,
        containerMotionProps,
        childMotionProps,
        enableContainer,
        enableChild,
        remountKey,
    } = useMotionControls({
        preset: normalizedPreset,
        duration: durationVal,
        editorPreview: EditorPreview,
        group: normalizedGroup === 'all' ? 'content' : normalizedGroup,
        keyBase: 'item-list',
    });

    const animateContent =
        enableAnim &&
        (normalizedGroup === 'all' || normalizedGroup === 'content');
    const animateFeatures =
        enableAnim &&
        (normalizedGroup === 'all' || normalizedGroup === 'features');
    const animateCTAs =
        enableAnim && (normalizedGroup === 'all' || normalizedGroup === 'ctas');

    const featureContainerEnabled =
        animateFeatures &&
        isStagger &&
        (normalizedGroup === 'all' ? enableAnim : enableContainer('features'));
    const featureChildEnabled =
        animateFeatures &&
        (normalizedGroup === 'all' ? enableAnim : enableChild('features'));
    const featureSubChildEnabled = featureChildEnabled && isStagger;
    const contentChildEnabled =
        animateContent &&
        (normalizedGroup === 'all' ? enableAnim : enableChild('content'));
    const ctaChildEnabled =
        animateCTAs &&
        (normalizedGroup === 'all' ? enableAnim : enableChild('ctas'));

    const featureChildMotionProps = (() => {
        if (!isStagger) return childMotionProps;
        // Let the stagger container drive child timing; just supply variants.
        return {
            variants: {
                hidden: { opacity: 0, y: 12 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: Math.max(0.3, durationVal - 0.1) },
                },
            },
        };
    })();

    return (
        <section
            id={Anchor}
            className={`editable-component relative px-6 lg:px-8 ${
                backgroundColor || overlayColor || ''
            } ${vSpace}`}
            style={backgroundStyle}
            data-json="itemlist"
        >
            <div
                className={`relative z-40 mx-auto flex max-w-7xl flex-col ${containerAlign}`}
            >
                {Header && (
                    <div className={`max-w-3xl ${textAlign}`}>
                        <MotionMaybe
                            enable={contentChildEnabled}
                            nodeKey={`${remountKey}-content`}
                            motionProps={childMotionProps}
                        >
                            <div className="mb-6 md:mb-10">
                                <div>
                                    <MarkDown
                                        className={`font-display text-base uppercase leading-7 tracking-wide ${taglineColor}`}
                                        components={{
                                            p: ({ children, ...rest }) => (
                                                <p
                                                    {...rest}
                                                    className={`font-display text-base uppercase leading-7 tracking-wide ${taglineColor}`}
                                                >
                                                    {children}
                                                </p>
                                            ),
                                        }}
                                    >
                                        {Tagline}
                                    </MarkDown>
                                </div>

                                <MarkDown
                                    variant="h2"
                                    className={`font-display text-2xl tracking-tight ${headerColor} md:text-3xl lg:text-4xl`}
                                >
                                    {Header}
                                </MarkDown>

                                {Body && (
                                    <MarkDown
                                        className={`mt-6 text-lg font-text leading-8 ${textColor}`}
                                    >
                                        {Body}
                                    </MarkDown>
                                )}
                            </div>
                        </MotionMaybe>
                    </div>
                )}

                {/* Item List */}
                <MotionMaybe
                    enable={featureContainerEnabled}
                    nodeKey={`${remountKey}-features`}
                    motionProps={containerMotionProps}
                    className={`mt-8 flex flex-wrap items-start gap-x-4 gap-y-8 md:gap-x-10 md:gap-y-12`}
                >
                    {Features.slice(0, 15).map((Feature, idx) => (
                        <MotionMaybe
                            key={`${Feature.Title}-${idx}`}
                            enable={featureChildEnabled}
                            nodeKey={`${remountKey}-feature-${idx}`}
                            motionProps={featureChildMotionProps}
                            className={`${cardAlign} flex min-w-38 max-w-38 flex-col md:min-w-52 md:max-w-52 `}
                        >
                            <MotionMaybe
                                enable={featureSubChildEnabled}
                                nodeKey={`${remountKey}-feature-${idx}-head`}
                                motionProps={featureChildMotionProps}
                                className={`flex ${
                                    Alignment === 'left'
                                        ? 'flex-row items-start text-left'
                                        : 'flex-col items-center text-center'
                                } ${iconAlign}`}
                            >
                                <i
                                    className={`${
                                        Feature.Icon
                                    } block text-lg ${iconColor} ${
                                        Alignment === 'left'
                                            ? 'mr-3 mt-1'
                                            : 'mb-3'
                                    }`}
                                />
                                <MarkDown
                                    variant={featureHeadingVariant}
                                    className={`md:text-md font-display text-base leading-tight ${textColor} mb-1`}
                                >
                                    {Feature.Title}
                                </MarkDown>
                            </MotionMaybe>

                            <MotionMaybe
                                enable={featureSubChildEnabled}
                                nodeKey={`${remountKey}-feature-${idx}-body`}
                                motionProps={featureChildMotionProps}
                            >
                                <div
                                    className={`${textColor} mt-2 text-xs leading-5 ${textAlign}`}
                                >
                                    <MarkDown>{Feature.Description}</MarkDown>
                                </div>
                            </MotionMaybe>
                        </MotionMaybe>
                    ))}
                </MotionMaybe>

                {/* Regular CTA */}
                {hasCTAs && (
                    <MotionMaybe
                        enable={ctaChildEnabled}
                        nodeKey={`${remountKey}-ctas`}
                        motionProps={childMotionProps}
                    >
                        {CTAs}
                    </MotionMaybe>
                )}
            </div>
        </section>
    );
}
